import { useMemo, useState } from 'react';
import { UPLOADED_TEMPLATE_PREVIEW_IMAGE_KEY } from '../Template.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { UploadedPreviewImage } from '../Template.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useDisclosure } from '@withjoy/joykit';
import { useGetEventMediaQuery } from '@graphql/generated';
import { MediaPhoto } from '../../Layout/components/HeaderPhotoCustomization/components/PhotoSelector/PhotoSelector.types';
import { getLocalStorage } from '@shared/core';
import { useWebsiteDesignerTelemetry } from '../../../WebsiteDesigner.telemetry';
import { AddMediaArgs } from '@apps/admin/routes/WebsiteOverview/routes/Photos/components/PhotoPickerDialog/PhotoPickerDialog.types';

const getUploadedPreviewImageKey = (eventId: string) => `${UPLOADED_TEMPLATE_PREVIEW_IMAGE_KEY}_${eventId}`;

const localStorage = getLocalStorage();

export const useUploadedPreviewImage = () => {
  const { eventInfo, eventHandle } = useEventInfo();
  const telemetry = useWebsiteDesignerTelemetry();

  const uploadedPreviewImageKey = useMemo(() => getUploadedPreviewImageKey(eventInfo?.eventId || ''), [eventInfo?.eventId]);

  const [uploadedPreviewImage, setUploadedPreviewImage] = useState<UploadedPreviewImage | null>(() => {
    const data = localStorage.getItem(uploadedPreviewImageKey) || '';
    if (!data) {
      return null;
    }

    try {
      const parsedData = JSON.parse(data) as UploadedPreviewImage;
      return {
        id: parsedData.id,
        url: parsedData.url,
        height: parsedData.height,
        width: parsedData.width,
      };
    } catch (error) {
      // if any parse error encountered, then that means the data is corrupted - so removing that key from local storage
      localStorage.setItem(uploadedPreviewImageKey);
      return null;
    }
  });

  const { data: eventMedia, loading: isLoadingEventMedia } = useGetEventMediaQuery({
    variables: {
      eventHandle: eventHandle || '',
    },
    skip: !eventHandle,
    batchMode: 'fast',
  });

  const media = useMemo(() => {
    const mediaPhotos = (eventMedia?.eventByName?.media?.filter(m => m.__typename === 'MediaPhoto') || []) as MediaPhoto[];
    return mediaPhotos?.sort((a, b) => {
      return b.createdAt.milliseconds - a.createdAt.milliseconds;
    });
  }, [eventMedia]);

  const { isOpen: isOpenPickerDialog, onOpen: onOpenPickerDialog, onClose: onClosePickerDialog } = useDisclosure();

  const handlePhotoSelect = useEventCallback(({ selectedItemsId, cb }: AddMediaArgs) => {
    const selectedImage = media.find(m => m.id === selectedItemsId[0]);
    if (selectedImage) {
      const data = {
        id: selectedImage.id,
        url: selectedImage.photo.url,
        height: selectedImage.photo.height,
        width: selectedImage.photo.width,
      };
      localStorage.setItem(uploadedPreviewImageKey, JSON.stringify(data));
      setUploadedPreviewImage(data);
    }
    cb();
    onClosePickerDialog();
  });

  const handleCustomPhotoUploadCTAClick = useEventCallback(() => {
    if (!uploadedPreviewImage) {
      telemetry.onViewWithPhotoCTAClick('viewWithYourPhoto');
      onOpenPickerDialog();
    } else {
      telemetry.onViewWithPhotoCTAClick('clearPhoto');
      localStorage.setItem(uploadedPreviewImageKey);
      setUploadedPreviewImage(null);
    }
  });

  return {
    uploadedPreviewImageKey,
    uploadedPreviewImage,
    isOpenPickerDialog,
    onClosePickerDialog,
    onOpenPickerDialog,
    media,
    isLoadingEventMedia,
    handlePhotoSelect,
    handleCustomPhotoUploadCTAClick,
  };
};
